import { StyleSheet, View, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import React, { useState, useEffect } from "react";
import { useRouter } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedPanel } from "@/components/ThemedPanel";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { getRules } from "common247";
import { runSetup, clearEmulatorData } from "@/services/firebase/setup-functions";
import { getServerStatus } from "@services/firebase/status-functions";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

type AdminCard = {
  title: string;
  description: string;
  icon: string;
  route?: string;
  onPress?: () => void;
  color: string;
  adminOnly?: boolean;
  testerOnly?: boolean;
};

const ADMIN_SECTIONS: AdminCard[] = [
  {
    title: "User Admin",
    description: "View all app users, their claims, and All Contacts group membership.",
    icon: "👤",
    route: "/admin/user-admin",
    color: "#4A90E2",
    adminOnly: true,
  },
  {
    title: "All Activity",
    description: "Inspect platform-wide activity logs, user logins, and object mutations.",
    icon: "⏱️",
    route: "/admin/activity-admin",
    color: "#6366F1",
    adminOnly: true,
  },
  {
    title: "Media Admin",
    description: "Browse and manage all media uploaded by any user.",
    icon: "🖼️",
    route: "/admin/media-admin",
    color: "#3498DB",
    adminOnly: true,
  },
  {
    title: "Group Admin",
    description: "Inspect all system-wide groups and their members.",
    icon: "👥",
    route: "/admin/group-admin",
    color: "#9B59B6",
    adminOnly: true,
  },
  {
    title: "Contact Admin",
    description: "View all registered contacts across the platform.",
    icon: "📇",
    route: "/admin/contact-admin",
    color: "#27AE60",
    adminOnly: true,
  },
  {
    title: "Memory Admin",
    description: "View and group all memories in the system by owner.",
    icon: "🧠",
    route: "/admin/memory-admin",
    color: "#F39C12",
    adminOnly: true,
  },
  {
    title: "Tag Admin",
    description: "View all tags across the platform grouped by user.",
    icon: "🏷️",
    route: "/admin/tag-admin",
    color: "#0D9488",
    adminOnly: true,
  },
  {
    title: "All Places",
    description: "Inspect and manage all places across the platform by owner.",
    icon: "📍",
    route: "/admin/place-admin",
    color: "#7E22CE",
    adminOnly: true,
  },
  {
    title: "All Anchors",
    description: "Inspect and manage all anchors across the platform by owner.",
    icon: "⚓",
    route: "/admin/anchor-admin",
    color: "#84CC16",
    adminOnly: true,
  },
  {
    title: "All Shares",
    description: "Inspect and manage all shared records across the entire platform.",
    icon: "🤝",
    route: "/admin/share-admin",
    color: "#E74C3C",
    adminOnly: true,
  },
  {
    title: "Magic Links",
    description: "View all sent invitation and password reset magic links.",
    icon: "🔗",
    route: "/admin/magic-link-admin",
    color: "#9B59B6",
    adminOnly: true,
  },
  {
    title: "Database Migrations",
    description: "View applied and pending database schema migrations, and execute pending migrations.",
    icon: "🔄",
    route: "/admin/migration-admin",
    color: "#16A085",
    adminOnly: true,
  },
  {
    title: "Crashlytics Admin",
    description: "Inspect Firebase Crashlytics runtime health, on-device tombstones, and test crash pipelines.",
    icon: "🔥",
    route: "/admin/crashlytics-admin",
    color: "#E67E22",
    adminOnly: true,
  },
  {
    title: "Feature Toggles",
    description: "Manage platform feature flags and gradual rollout toggles powered by Firebase Remote Config.",
    icon: "🎛️",
    route: "/admin/feature-toggles",
    color: "#2980B9",
    adminOnly: true,
  },
  {
    title: "All Workflows",
    description: "Track all workflow instances, active tasks, and assigned users across the platform.",
    icon: "⚡",
    route: "/admin/workflow-admin",
    color: "#0D9488",
    adminOnly: true,
  },
  {
    title: "Platform Metrics",
    description: "Inspect live platform metrics, database storage breakdown per Firestore collection, and growth rates.",
    icon: "📊",
    route: "/admin/metrics-admin",
    color: "#0284C7",
    adminOnly: true,
  },
  {
    title: "Design Sandbox",
    description: "Interactive component design harnesses, color tokens, and UI/UX workbenches.",
    icon: "🎨",
    route: "/admin/design",
    color: "#8E44AD",
    testerOnly: true,
  },
  {
    title: "Test Tools",
    description: "Developer testing dashboards, logging cache flushes, and alert system validation.",
    icon: "🧪",
    route: "/admin/test",
    color: "#D35400",
    adminOnly: true,
  },
];

export default function Index() {
  const router = useRouter();
  const services = useFirebase();
  const { user } = useAuth(true);
  const [isEmulator, setIsEmulator] = useState(false);
  const [environment, setEnvironment] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [runningSetup, setRunningSetup] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  const textMuted = useThemeColor({}, "textMuted");

  useEffect(() => {
    const checkStatus = async () => {
      if (!services) return;
      try {
        const status = await getServerStatus(services);
        setIsEmulator(status.isEmulator);
        setEnvironment(status.environment);
      } catch (error) {
        console.error("Error checking server status:", error);
      } finally {
        setCheckingStatus(false);
      }
    };
    checkStatus();
  }, [services]);

  if (!user) return null;

  const rules = getRules();
  const isAdmin = rules.isAdmin.can(user.claims, undefined);
  const isTester = rules.isTester.can(user.claims, undefined);

  const handleRunSetup = async () => {
    if (!services) return;
    setRunningSetup(true);
    try {
      const response = await runSetup(services);
      if (response.status) {
        CustomAlert.alert("Success", response.message);
      } else {
        CustomAlert.alert("Error", response.message);
      }
    } catch (error: any) {
      console.error("Error running setup:", error);
      CustomAlert.alert("Error", error.message || "Failed to run setup.");
    } finally {
      setRunningSetup(false);
    }
  };

  const handleClearData = async () => {
    if (!services) return;
    setClearingData(true);
    try {
      const response = await clearEmulatorData(services);
      if (response.status) {
        CustomAlert.alert("Success", response.message);
      } else {
        CustomAlert.alert("Error", response.message);
      }
    } catch (error: any) {
      console.error("Error clearing data:", error);
      CustomAlert.alert("Error", error.message || "Failed to clear data.");
    } finally {
      setClearingData(false);
    }
  };

  const visibleSections = ADMIN_SECTIONS.filter((s) => {
    if (s.adminOnly && !isAdmin) return false;
    if (s.testerOnly && !isTester && !isAdmin) return false;
    return true;
  });

  const emulatorSections: AdminCard[] = isEmulator
    ? [
      {
        title: "Setup Demo Data",
        description: "Seed the database with test contacts, groups, media, memories and shares.",
        icon: "⚡",
        onPress: handleRunSetup,
        color: "#F1C40F",
        adminOnly: true,
      },
      {
        title: "Clear Demo Data",
        description: "Remove all system data (groups, media, contacts, memories, shares).",
        icon: "🗑️",
        onPress: handleClearData,
        color: "#E74C3C",
        adminOnly: true,
      },
    ]
    : [];

    const allCards = [...visibleSections, ...emulatorSections];
    const pairedSections: AdminCard[][] = [];
    for (let i = 0; i < allCards.length; i += 2) {
      pairedSections.push(allCards.slice(i, i + 2));
    }

    return (
      <ThemedScreen title="Admin">
        <ThemedView style={styles.container}>
          <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
            {`LOGGED IN AS: ${(user.displayName || user.email || "").toUpperCase()}${isEmulator ? " (EMULATOR)" : ""}${environment ? ` [${environment.toUpperCase()}]` : ""}`}
          </ThemedText>

          <ThemedProgressBar loading={checkingStatus} />
          {!checkingStatus && (
            <ScrollView contentContainerStyle={styles.grid} showsVerticalScrollIndicator={false}>
              {pairedSections.map((row, rowIdx) => (
                <View key={`row-${rowIdx}`} style={styles.row}>
                  {row.map((section, idx) => (
                    <TouchableOpacity
                      key={section.route || `action-${rowIdx}-${idx}`}
                      style={styles.cardWrapper}
                      onPress={() => {
                        if (section.route) {
                          router.push(section.route as any);
                        } else if (section.onPress) {
                          section.onPress();
                        }
                      }}
                      disabled={(runningSetup && section.title === "Run Setup") || (clearingData && section.title === "Clear Data")}
                      activeOpacity={0.75}
                    >
                      <ThemedPanel padding={10} elevation="low" contentStyle={styles.cardContentRow} style={styles.cardPanel}>
                        <View style={[styles.iconBadge, { backgroundColor: section.color + "18" }]}>
                          {(runningSetup && section.title === "Run Setup") || (clearingData && section.title === "Clear Data") ? (
                            <ActivityIndicator size="small" color={section.color} />
                          ) : (
                            <ThemedText style={styles.icon}>{section.icon}</ThemedText>
                          )}
                        </View>
                        <View style={styles.cardContent}>
                          <ThemedText type="defaultSemiBold" style={styles.cardTitle} numberOfLines={1}>
                            {section.title}
                          </ThemedText>
                          <ThemedText style={styles.cardDesc} numberOfLines={1}>
                            {section.description}
                          </ThemedText>
                        </View>
                        <View style={[styles.arrow, { borderColor: section.color + "50" }]}>
                          <ThemedText style={[styles.arrowText, { color: section.color }]}>{section.route ? "›" : "▶"}</ThemedText>
                        </View>
                      </ThemedPanel>
                    </TouchableOpacity>
                  ))}
                  {row.length === 1 && <View style={[styles.cardWrapper, { opacity: 0 }]} pointerEvents="none" />}
                </View>
              ))}
            </ScrollView>
          )}
        </ThemedView>
      </ThemedScreen>
    );
  }

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
  },
  grid: {
    gap: 8,
    paddingBottom: 16,
  },
  row: {
    flexDirection: "row",
    gap: 8,
    alignItems: "stretch",
  },
  cardWrapper: {
    flex: 1,
  },
  cardPanel: {
    flex: 1,
    height: "100%",
  },
  cardContentRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  iconBadge: {
    width: 38,
    height: 38,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  icon: {
    fontSize: 20,
  },
  cardContent: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 14,
  },
  cardDesc: {
    fontSize: 11,
    opacity: 0.65,
    lineHeight: 15,
  },
  arrow: {
    width: 24,
    height: 24,
    borderRadius: 0,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  arrowText: {
    fontSize: 16,
    lineHeight: 18,
    fontWeight: "bold",
  },
  loader: {
    marginTop: 40,
  },
});
